import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hideIntalkWidget, setIntalkIframeReady, setIntalkIframeUrl, setCallInProgress } from '../../actions/intalk';
import { sendToastMessage } from '../../actions/toast';
import axios from 'axios';
import Cookies from 'universal-cookie';
import production from '../../config/env/production';
import config from '../../config';
import { getIntalkCredentials } from '../../actions/auth';

const cookies = new Cookies();

const getOriginFromUrl = (url) => {
    try {
        if (!url) return undefined;
        const urlObj = new URL(url);
        return urlObj.origin;
    } catch (e) {
        return undefined;
    }
};

// Modern loading spinner component
const ModernSpinner = () => (
    <div className="relative">
        <div className="w-16 h-16 border-4 border-blue-100 border-t-blue-500 rounded-full animate-spin"></div>
        <div className="absolute inset-0 w-16 h-16 border-4 border-transparent border-r-blue-300 rounded-full animate-ping"></div>
    </div>
);

// Phone icon SVG
const PhoneIcon = ({ className = "w-6 h-6" }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
        <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
    </svg>
);

const BackspaceIcon = ({ className = "w-5 h-5" }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
        <path fillRule="evenodd" d="M6.707 4.879A3 3 0 018.828 4H15a3 3 0 013 3v6a3 3 0 01-3 3H8.828a3 3 0 01-2.12-.879l-4.415-4.414a1 1 0 010-1.414l4.414-4.414zm4 1.414L9.414 7.586 11 9.172 12.586 7.586 14.172 9.172 12.586 10.758 14.172 12.344 12.586 13.93 11 12.344 9.414 13.93 7.828 12.344 9.414 10.758 7.828 9.172 9.414 7.586z" clipRule="evenodd" />
    </svg>
);

// Minimize icon SVG
const MinimizeIcon = ({ className = "w-5 h-5", isMinimized = false }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 20 20">
        {isMinimized ? (
            // Maximize/Expand icon (square with corners)
            <path fillRule="evenodd" d="M3 4a1 1 0 011-1h4a1 1 0 010 2H6.414l2.293 2.293a1 1 0 01-1.414 1.414L5 6.414V8a1 1 0 01-2 0V4zm9 1a1 1 0 010-2h4a1 1 0 011 1v4a1 1 0 01-2 0V6.414l-2.293 2.293a1 1 0 01-1.414-1.414L13.586 5H12zm-9 7a1 1 0 012 0v1.586l2.293-2.293a1 1 0 011.414 1.414L6.414 15H8a1 1 0 010 2H4a1 1 0 01-1-1v-4zm13-1a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 010-2h1.586l-2.293-2.293a1 1 0 011.414-1.414L15 13.586V12a1 1 0 011-1z" clipRule="evenodd" />
        ) : (
            // Minimize icon (horizontal line)
            <path fillRule="evenodd" d="M3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z" clipRule="evenodd" />
        )}
    </svg>
);

const IntalkGlobalWidget = () => {
    const dispatch = useDispatch();
    const { isVisible, callData, isIframeReady, iframeUrl, isCallInProgress } = useSelector(state => state.intalk);
    const ticket_number = callData?.ticket_number;

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showInitialPage, setShowInitialPage] = useState(true);
    const [isMinimized, setIsMinimized] = useState(false);
    const iframeRef = useRef(null);
    const newLoggedUserData = useSelector(state => state.auth.newLoggedUserData);
    const intalkCredentials = useSelector(state => state.auth.intalkCredentials);

    useEffect(() => {
        if (!intalkCredentials?.token) {
            dispatch(getIntalkCredentials());
        }
    }, [dispatch, intalkCredentials?.token]);

    useEffect(() => {
        if (!isVisible || isIframeReady || !intalkCredentials.token) {

        } else {
            const initializeIntalk = async () => {
                let username = config.name === production.name ? newLoggedUserData.user.first_name : 'Tahoora';;
                if (newLoggedUserData?.user?.custom_fields?.intalk?.username) {
                    username = newLoggedUserData.user.custom_fields?.intalk?.username;
                }

                try {
                    const response = await axios.post(
                        intalkCredentials.api_base_url,
                        {
                            token: intalkCredentials.token,
                            username,
                            location_id: intalkCredentials.secret_key
                        },
                        {
                            headers: {
                                'Content-Type': 'application/json'
                            },
                            handlerEnabled: false
                        }
                    );

                    if (response.data && response.data.status === "1") {
                        const userToken = response.data.data.user_token;
                        const newIframeUrl = response.data.data.intalk_iframe_url;

                        dispatch(setIntalkIframeUrl(newIframeUrl));
                        dispatch(setIntalkIframeReady(true));

                        // Show success message
                        dispatch(sendToastMessage({
                            status: 'success',
                            message: 'Call service initialized successfully!'
                        }));
                    } else {
                        const errorMsg = 'Login failed: ' + (response.data.error || response.data.message);
                        setError(errorMsg);
                        console.error('Login failed:', response.data.error || response.data.message);

                        dispatch(sendToastMessage({
                            status: 'danger',
                            message: 'Failed to initialize call service. Please try again.'
                        }));
                    }
                } catch (error) {
                    const errorMsg = 'Connection error: ' + error.message;
                    setError(errorMsg);
                    console.error('Login error:', error);

                    dispatch(sendToastMessage({
                        status: 'danger',
                        message: 'Unable to connect to call service. Please check your internet connection and try again.'
                    }));
                } finally {
                    setLoading(false);
                }
            };

            if (isVisible) {
                setLoading(true);
                setError(null);
                initializeIntalk();
            }
        }
    }, [dispatch, isVisible, isIframeReady, newLoggedUserData.user.first_name, intalkCredentials?.token, intalkCredentials?.api_base_url, intalkCredentials?.secret_key, newLoggedUserData?.user?.custom_fields?.intalk?.username]);

    // Listen for messages from iframe (call status updates)
    useEffect(() => {
        const handleMessage = (event) => {
            // Ensure message is from Intalk domain
            if (event.origin !== getOriginFromUrl(intalkCredentials?.api_base_url)) {
                return;
            }

            const { type, data } = event.data;

            switch (type) {
                case 'call_ended':
                case 'call_failed':
                case 'call_disconnected':
                    dispatch(setCallInProgress(false));
                    break;
                case 'call_connected':
                case 'call_started':
                    dispatch(setCallInProgress(true));
                    break;
                default:
                    // Handle other message types if needed
                    break;
            }
        };

        window.addEventListener('message', handleMessage);
        return () => window.removeEventListener('message', handleMessage);
    }, [dispatch, intalkCredentials?.api_base_url]);

    const handleClose = () => {
        // Send hangup message to iframe before closing if call is in progress
        if (iframeRef.current && iframeUrl && isCallInProgress) {
            const hangupMessage = {
                type: 'call_hangup'
            };
            iframeRef.current.contentWindow.postMessage(hangupMessage, getOriginFromUrl(intalkCredentials?.api_base_url));
        }

        dispatch(hideIntalkWidget({}));
        setShowInitialPage(true);
        setIsMinimized(false);
    };

    const handleMinimize = () => {
        setIsMinimized(!isMinimized);
    };

    const handleCallInitiated = ({ phone_number, first_name, last_name, contact_unique_id }) => {
        if (!iframeRef.current) {
            console.error('iFrame reference not available');
            return;
        }

        if (!phone_number || !phone_number.trim()) {
            setError('Please enter a valid phone number');
            return;
        }

        const cleanedPhoneNumber = phone_number.replace(/\D/g, '');
        if (cleanedPhoneNumber.length < 10) {
            setError('Please enter a valid phone number');
            return;
        }

        const message = {
            type: 'click_to_call',
            data: {
                phone_number,
                first_name,
                last_name,
                contact_unique_id
            }
        };

        const toPostMessage = getOriginFromUrl(intalkCredentials?.api_base_url);

        try {
            iframeRef.current.contentWindow.postMessage(message, toPostMessage);
            dispatch(setCallInProgress(true));
            setShowInitialPage(false);
            console.log('toPostMessage In Try Block:', toPostMessage)
            console.log('message, sendOn In Try Block:', message, toPostMessage)
        } catch (error) {
            console.error('Error posting message:', error);
        }
        console.log('-------FOR CHECK--------')
        console.log('-------FOR CHECK--------')
        console.log('-------FOR CHECK--------')
        console.log('message, sendOn:', message, toPostMessage)
        console.log('-------FOR CHECK--------')
        console.log('-------FOR CHECK--------')
        console.log('-------FOR CHECK--------')
    };

    const handleManualCall = () => {
        setShowInitialPage(true);
    };

    return (
        <div className={`fixed bottom-7 left-8 z-50 transition-all duration-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full pointer-events-none'}`}>
            <div className={`w-[33rem] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden transition-all duration-300 ${isMinimized ? 'h-auto' : ''}`} style={{ height: isMinimized ? 'auto' : '42rem' }}>
                {/* Header */}
                <div className={`bg-gradient-to-r ${isCallInProgress ? 'from-green-500 to-green-600' : 'from-blue-400 to-blue-500'} p-4 text-white flex justify-between items-center`}>
                    <div className="flex items-center space-x-2 flex-1 min-w-0">
                        <PhoneIcon className={`w-5 h-5 ${isCallInProgress ? 'animate-pulse' : ''}`} />
                        <div className="flex flex-col min-w-0 flex-1">
                            <div className="flex items-center space-x-2">
                                <span className="font-semibold text-md tracking-wide">
                                    {isCallInProgress ? 'Call in Progress' : 'Call'}
                                    {ticket_number && (
                                        <span className="pl-2">
                                            : {ticket_number}
                                        </span>
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={handleMinimize}
                            className="text-white hover:text-gray-200 transition-colors"
                            title={isMinimized ? "Maximize" : "Minimize"}
                        >
                            <MinimizeIcon className="w-5 h-5" isMinimized={isMinimized} />
                        </button>
                        <button
                            onClick={handleClose}
                            className="text-white hover:text-gray-200 transition-colors"
                            title="Close"
                        >
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className={`relative transition-all duration-300 ${isMinimized ? 'h-0 overflow-hidden' : 'h-full'}`}>
                    {loading ? (
                        <div className="h-full p-8 flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
                            <div className="bg-white rounded-full p-8 shadow-2xl mb-6 animate-pulse">
                                <ModernSpinner />
                            </div>
                            <div className="text-center space-y-3">
                                <h3 className="text-xl font-semibold text-gray-800">
                                    Connecting to Intalk
                                </h3>
                                <p className="text-gray-600">
                                    Please wait while we establish a secure connection...
                                </p>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="p-8 h-full flex flex-col justify-center">
                            <div className="text-center">
                                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <svg className="w-8 h-8 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">Connection Failed</h3>
                                <p className="text-gray-600 mb-6">{error}</p>
                                <button
                                    onClick={handleClose}
                                    className="px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-xl hover:from-red-600 hover:to-red-700 transition-all duration-200"
                                >
                                    Close & Try Again
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Iframe - always loaded but hidden when showing initial page */}
                            <iframe
                                ref={iframeRef}
                                height="100%"
                                width="100%"
                                src={iframeUrl}
                                className={`w-full h-full border-0 transition-opacity duration-500 ${showInitialPage ? 'opacity-0 absolute' : 'opacity-100'}`}
                                title="Intalk Service"
                                allow="microphone; camera"
                            />

                            {/* Initial Page - shown when manual dialing */}
                            {showInitialPage && (
                                <div className="absolute inset-0 bg-white" style={{ height: '39rem' }}>
                                    <InitialPage
                                        phoneNumber={callData?.phone_number || ''}
                                        first_name={callData?.first_name || ''}
                                        last_name={callData?.last_name || ''}
                                        contact_unique_id={callData?.contact_unique_id || ''}
                                        onCallInitiated={handleCallInitiated}
                                        setError={setError}
                                    />
                                </div>
                            )}

                            {/* Manual dial button - shown when iframe is ready and no initial page */}
                            {!showInitialPage && (
                                <div className="absolute bottom-4 right-4">
                                    <button
                                        onClick={handleManualCall}
                                        className="bg-blue-500 hover:bg-blue-600 text-white p-3 rounded-full shadow-lg transition-colors"
                                        title="Manual Dial"
                                    >
                                        <PhoneIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default IntalkGlobalWidget;

const InitialPage = ({ phoneNumber: initialPhoneNumber, first_name, last_name, contact_unique_id, onCallInitiated, setError }) => {
    const [phoneNumber, setPhoneNumber] = React.useState(initialPhoneNumber || '');
    const [isDialing, setIsDialing] = React.useState(false);
    const [pressedKey, setPressedKey] = React.useState(null);

    React.useEffect(() => {
        if (initialPhoneNumber) {
            setPhoneNumber(initialPhoneNumber);
        }
    }, [initialPhoneNumber]);

    const formatPhoneNumber = (value) => {
        const cleaned = value.replace(/\D/g, '');
        if (cleaned.length <= 3) return cleaned;
        if (cleaned.length <= 6) return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
        return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6, 10)}`;
    };

    const handleNumberChange = (value) => {
        const cleaned = value.replace(/\D/g, '');
        if (cleaned.length <= 10) {
            setPhoneNumber(cleaned);
        }
    };

    const handleKeyPress = (key) => {
        if (phoneNumber.replace(/\D/g, '').length < 10) {
            setPressedKey(key);
            setTimeout(() => setPressedKey(null), 150);
            setPhoneNumber(prevNumber => {
                const cleaned = prevNumber.replace(/\D/g, '') + key;
                return cleaned;
            });
        }
    };

    const handleBackspace = () => {
        setPhoneNumber(prevNumber => {
            const cleaned = prevNumber.replace(/\D/g, '');
            return cleaned.slice(0, -1);
        });
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            initiateCall();
        }
    };

    const initiateCall = async () => {
        if (!phoneNumber.trim()) {
            setError('Please provide a valid phone number');
            return;
        }

        const cleanedPhoneNumber = phoneNumber.replace(/\D/g, '');
        if (cleanedPhoneNumber.length < 10) {
            setError('Please enter a valid 10-digit phone number');
            return;
        }

        setError(null);
        setIsDialing(true);

        if (onCallInitiated) {
            onCallInitiated({
                phone_number: cleanedPhoneNumber,
                first_name,
                last_name,
                contact_unique_id
            });
        }
        setIsDialing(false);
    };

    const dialpadButtons = [
        { key: '1', letters: '' },
        { key: '2', letters: 'ABC' },
        { key: '3', letters: 'DEF' },
        { key: '4', letters: 'GHI' },
        { key: '5', letters: 'JKL' },
        { key: '6', letters: 'MNO' },
        { key: '7', letters: 'PQRS' },
        { key: '8', letters: 'TUV' },
        { key: '9', letters: 'WXYZ' },
        { key: '*', letters: '' },
        { key: '0', letters: '+' },
        { key: '#', letters: '' }
    ];

    const isCallReady = phoneNumber.replace(/\D/g, '').length >= 10;

    return (
        <div className='w-full h-full flex flex-col p-2 sm:p-4 bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 relative overflow-hidden'>
            {/* Animated background elements */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-4 -left-4 w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full opacity-20 animate-pulse"></div>
                <div className="absolute top-1/4 -right-6 w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full opacity-15 animate-bounce" style={{ animationDuration: '3s' }}></div>
                <div className="absolute -bottom-4 left-1/4 w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-green-400 to-blue-400 rounded-full opacity-25 animate-pulse" style={{ animationDelay: '1s' }}></div>
            </div>

            {/* Header with contact info - compact */}
            {(first_name || last_name) && (
                <div className="mb-2 sm:mb-3 text-center z-10 flex-shrink-0">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-1 sm:mb-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                        <span className="text-white text-xs sm:text-sm font-bold">
                            {(first_name?.[0] || '') + (last_name?.[0] || '')}
                        </span>
                    </div>
                    <h2 className="text-sm sm:text-base font-semibold text-gray-800 mb-1">
                        {first_name} {last_name}
                    </h2>
                </div>
            )}

            {/* Phone Display Section - compact */}
            <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-xl w-full border border-white/50 mb-2 sm:mb-3 p-2 sm:p-3 z-10 flex-shrink-0">
                <div className="text-center">
                    <div className="relative w-full">
                        <input
                            ref={(input) => input && input.focus()}
                            type="text"
                            value={formatPhoneNumber(phoneNumber)}
                            onChange={(e) => handleNumberChange(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="text-base sm:text-lg font-light text-gray-800 text-center w-full bg-transparent border-none outline-none placeholder-gray-400 tracking-wider"
                            placeholder="000 000-0000"
                            maxLength={14}
                        />
                        {phoneNumber && (
                            <button
                                onClick={handleBackspace}
                                className="absolute right-1 sm:right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-all duration-200"
                            >
                                <BackspaceIcon className="w-6 h-6" />
                            </button>
                        )}
                    </div>
                    {/* Animated underline */}
                    <div className={`h-0.5 mx-auto mt-1 sm:mt-2 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full transition-all duration-500 ${phoneNumber ? 'w-full opacity-100' : 'w-0 opacity-0'}`}></div>
                </div>
            </div>

            {/* Enhanced Dialpad Section - flexible height */}
            <div className="bg-white/80 backdrop-blur-sm rounded-xl sm:rounded-2xl shadow-xl p-2 sm:p-3 mb-2 sm:mb-3 border border-white/50 w-full z-10 flex-1 min-h-0">
                <div className="grid grid-cols-3 gap-1 sm:gap-2 h-full">
                    {dialpadButtons.map((button, index) => (
                        <button
                            key={button.key}
                            onClick={() => handleKeyPress(button.key)}
                            className={`relative w-full rounded-lg sm:rounded-xl bg-gradient-to-b from-white to-gray-50 border border-gray-200/50 shadow-md hover:shadow-lg hover:from-gray-50 hover:to-gray-100 active:scale-95 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 group ${pressedKey === button.key ? 'scale-95 bg-gradient-to-b from-blue-100 to-blue-200' : ''
                                }`}
                            style={{ minHeight: '35px' }}
                        >
                            <div className="flex flex-col items-center justify-center h-full py-0.5 sm:py-1">
                                <span className={`text-base sm:text-lg font-bold transition-colors duration-200 ${pressedKey === button.key ? 'text-blue-600' : 'text-gray-800 group-hover:text-blue-600'
                                    }`}>
                                    {button.key}
                                </span>
                                {button.letters && (
                                    <span className={`text-xs font-medium transition-colors duration-200 leading-none ${pressedKey === button.key ? 'text-blue-500' : 'text-gray-500 group-hover:text-blue-500'
                                        }`}>
                                        {button.letters}
                                    </span>
                                )}
                            </div>
                            {/* Ripple effect */}
                            <div className="absolute inset-0 rounded-lg sm:rounded-xl overflow-hidden">
                                <div className={`absolute inset-0 bg-blue-400 opacity-0 group-active:opacity-20 transition-opacity duration-150 ${pressedKey === button.key ? 'opacity-20' : ''
                                    }`}></div>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            {/* Enhanced Call Button Section - compact */}
            <div className="flex flex-col items-center z-10 flex-shrink-0">
                <button
                    onClick={initiateCall}
                    disabled={isDialing || !phoneNumber.replace(/\D/g, '') || phoneNumber.replace(/\D/g, '').length < 10}
                    className={`group relative h-12 w-12 sm:h-14 sm:w-14 rounded-full shadow-xl transition-all duration-300 transform hover:scale-110 active:scale-95 focus:outline-none focus:ring-4 focus:ring-green-500/30 ${isCallReady && !isDialing
                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 animate-pulse'
                        : 'bg-gradient-to-r from-gray-400 to-gray-500 cursor-not-allowed'
                        }`}
                >
                    {isDialing ? (
                        <div className="flex items-center justify-center h-full">
                            <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-full text-white">
                            <PhoneIcon className="w-4 h-4 sm:w-5 sm:h-5 transform group-hover:rotate-12 transition-transform duration-200" />
                        </div>
                    )}

                    {/* Pulsing ring animation for ready state */}
                    {isCallReady && !isDialing && (
                        <div className="absolute inset-0 rounded-full border-2 border-green-400 animate-ping opacity-75"></div>
                    )}
                </button>

                {/* Enhanced Status text with animations - compact */}
                <div className="mt-1 sm:mt-2 text-center">
                    <p className={`text-xs font-medium transition-all duration-500 ${phoneNumber.replace(/\D/g, '').length < 10
                        ? 'text-gray-500'
                        : isDialing
                            ? 'text-blue-600 animate-pulse'
                            : 'text-green-600'
                        }`}>
                        {phoneNumber.replace(/\D/g, '').length < 10
                            ? 'Enter a 10-digit phone number'
                            : isDialing
                                ? 'Connecting...'
                                : '✓ Ready to call'
                        }
                    </p>

                    {/* Progress indicator - compact */}
                    <div className="mt-1 w-20 sm:w-24 h-0.5 bg-gray-200 rounded-full overflow-hidden mx-auto">
                        <div
                            className="h-full bg-gradient-to-r from-blue-500 to-green-500 rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${Math.min((phoneNumber.replace(/\D/g, '').length / 10) * 100, 100)}%` }}
                        ></div>
                    </div>
                </div>
            </div>
        </div>
    );
};
